import { useCallback } from "react";
import { Box } from "@mui/material";
import {
  bitCoinSymbol,
  ethereumIcon,
  fill,
  noPendingTransactionIcon,
  solanaIcon,
  tronIcon,
  usdtLightningIcon,
  tonIcon,
  currencyImages,
} from "./images";
import Text from "./text/Text";
import {
  noPendingConfirmations,
  noPendingConfirmationsSubMsg,
  receiving,
  sats,
  sending,
  swapping,
  usdt,
} from "./messages";
import {
  dateTimeFormat,
  displayTaprootCurrency,
  getAmountFormatted,
  getCurrencyAmount,
  getDisplayCurrencySymbol,
  getPaidPaymentMethod,
  lightningLabel,
  onChainLabel,
  ethereumLabel,
  tronLabel,
  solanaLabel,
  tonLabel,
} from "./constants";
import ListItemSkeleton from "./ListItemSkeleton";
import { useNavigate } from "react-router-dom";
import ErrorMessageContent from "./ErrorMessageContent";
import { useSelector } from "react-redux";
import { useSession } from "../hooks/useSession";
import usePendingTransactions from "../hooks/usePendingTransactions";

function PendingTransactionsCommon({ customClass, isFromBrowser }) {
  const navigate = useNavigate();
  const { session } = useSession();

  const { taprootAsset, showBtcAmount } = useSelector((state) => state.common);
  const { uuid: currency } = { ...taprootAsset };
  const userDateFormat = session?.user?.date_format || "DD/MM/YYYY";

  const { transactions: allUnconfirmedTransactions, isLoading } =
    usePendingTransactions(currency);

  const handleOpenDetailPage = (transaction) => {
    localStorage.setItem(
      "transactionDetail",
      JSON.stringify({
        type: transaction?.object === "swap" ? "swap in" : transaction?.object,
        id: transaction?.id,
        confirmed: false,
      })
    );
    navigate(`/pending-confirmations/${transaction.id}`);
  };

  const transactionIcon = useCallback(
    (paymentMethod, transaction) => {
      if (paymentMethod === onChainLabel) {
        return bitCoinSymbol;
      } else if (
        paymentMethod === lightningLabel &&
        transaction?.target_currency === usdt
      ) {
        return usdtLightningIcon;
      } else if (paymentMethod === lightningLabel) {
        return fill;
      } else if (paymentMethod === ethereumLabel) {
        return ethereumIcon;
      } else if (paymentMethod === solanaLabel) {
        return solanaIcon;
      } else if (paymentMethod === tronLabel) {
        return tronIcon;
      } else if (paymentMethod === tonLabel) {
        return tonIcon;
      } else {
        return currencyImages[paymentMethod?.toUpperCase()];
      }
    },
    [allUnconfirmedTransactions]
  );

  return (
    <Box className={`transaction-container ${customClass}`}>
      {isLoading ? (
        <ListItemSkeleton />
      ) : allUnconfirmedTransactions.length > 0 ? (
        allUnconfirmedTransactions?.map((transaction, index) => {
          const paymentMethod = getPaidPaymentMethod(transaction);
          const isTransactionPayment =
            transaction?.object?.toLowerCase() === "payment";
          const isSwap = transaction?.object === "swap";

          const isOpenDetailPage =
            isTransactionPayment ||
            transaction?.object?.toLowerCase() === "instant_send" ||
            transaction?.object?.toLowerCase() === "withdraw" ||
            transaction?.object?.toLowerCase() === "swap";

          let amountToDisplay = isSwap
            ? transaction?.target_amount_swapped
            : isTransactionPayment
              ? transaction?.target_amount_paid
              : transaction?.target_amount;

          let resultAmount = isSwap
            ? transaction?.target_amount_swapped
            : isTransactionPayment
              ? amountToDisplay - transaction?.speed_fee?.total_fee
              : amountToDisplay + transaction?.speed_fee?.total_fee;
          let amount = getCurrencyAmount(
            currency === sats ? Math.floor(resultAmount) : resultAmount,
            currency
          );

          return (
            <Box
              className="transaction"
              key={index}
              onClick={() => {
                isOpenDetailPage && handleOpenDetailPage(transaction);
              }}
              sx={{
                borderBottom: "1px solid #f6f3f3",
                ...(isOpenDetailPage ? { cursor: "pointer" } : {}),
              }}
            >
              <Box className="left-side">
                <img
                  src={transactionIcon(paymentMethod, transaction)}
                  height={30}
                  width={30}
                />

                <Box ml="14px">
                  <Text className="grey-text" size={12} font="regular">
                    {dateTimeFormat(transaction?.created, userDateFormat)}
                  </Text>
                  <Text className="default-text" size={16} font="semibold">
                    {isSwap
                      ? swapping
                      : isTransactionPayment
                        ? receiving
                        : sending}
                  </Text>
                </Box>
              </Box>
              <Box className="right-side" textAlign="right">
                <Box ml="14px">
                  <Text
                    className="grey-text"
                    size={16}
                    font="bold"
                    sx={{
                      color: isSwap
                        ? "#0CAD62 !important"
                        : isTransactionPayment
                          ? "#0CAD62 !important"
                          : "#FD371E !important",
                    }}
                  >
                    {amount > 0
                      ? isSwap || isTransactionPayment
                        ? "+"
                        : "-"
                      : ""}

                    {getDisplayCurrencySymbol(currency, showBtcAmount)}

                    {getAmountFormatted(amount, currency)}
                  </Text>
                  <Text className="default-text" size={14} font="semibold">
                    {displayTaprootCurrency(amount, currency)}
                  </Text>
                </Box>
              </Box>
            </Box>
          );
        })
      ) : (
        <ErrorMessageContent
          img={noPendingTransactionIcon}
          msg={noPendingConfirmations}
          subMsg={noPendingConfirmationsSubMsg}
          padding={isFromBrowser}
        />
      )}
    </Box>
  );
}

export default PendingTransactionsCommon;
